/**
 * Browser language detection utilities
 * This file provides helpers to detect browser language preferences
 */

/**
 * Gets the browser's preferred languages as an array
 * @returns {string[]} Array of language codes in order of preference
 */
export const getBrowserLanguages = () => {
  // navigator.languages is an array of preferred languages in order (Chrome, Firefox, Edge)
  // navigator.language is the primary language (fallback for Safari, IE)
  return navigator.languages || [navigator.language || navigator.userLanguage || 'en'];
};

/**
 * Gets the browser's primary preferred language
 * @returns {string} The primary language code (e.g., 'en-US', 'es', 'fr')
 */
export const getPrimaryBrowserLanguage = () => {
  const languages = getBrowserLanguages();
  return languages[0] || 'en';
};

/**
 * Gets the browser's primary preferred language without region code
 * @returns {string} The primary language without region (e.g., 'en', 'es', 'fr')
 */
export const getPrimaryBrowserLanguageBase = () => {
  const primaryLanguage = getPrimaryBrowserLanguage();
  // Split by dash and take the first part (language code without region)
  return primaryLanguage.split('-')[0];
};

/**
 * Checks if the browser has a specific language in its preferences
 * @param {string} languageCode - The language code to check for (e.g., 'en', 'es')
 * @returns {boolean} True if the browser has this language in its preferences
 */
export const browserPrefersLanguage = (languageCode) => {
  if (!languageCode) return false;
  
  const lowerLanguageCode = languageCode.toLowerCase();
  return getBrowserLanguages().some(lang => 
    lang.toLowerCase() === lowerLanguageCode || 
    lang.toLowerCase().startsWith(lowerLanguageCode + '-')
  );
};

/**
 * Get debug information about browser language settings
 * Useful for troubleshooting language detection issues
 * @returns {Object} Object containing browser language information
 */
export const getBrowserLanguageDebugInfo = () => {
  return {
    preferredLanguages: getBrowserLanguages(),
    primaryLanguage: getPrimaryBrowserLanguage(),
    primaryLanguageBase: getPrimaryBrowserLanguageBase(),
    navigatorLanguageProperty: navigator.language,
    navigatorLanguagesProperty: navigator.languages,
    userAgentLanguage: navigator.userLanguage
  };
};